// SPDX-License-Identifier: GPL-3.0-or-later
// The lamp show: extra glow on top of the lamp states that the rules set. The rules own the
// states (off, on, blinking). This file only writes `accent` and `tint` of each insert.
//
// Four effects, each a state of the machine below:
// - hit: the lamp of a hit element glows and fades (HIT_MS). One lamp glows again after HIT_GAP_MS
//   at the earliest, so a lamp lights at most 3 times in one second.
// - attract: when no game runs, two coloured waves of light run down the table, one lamp at a time.
//   Each wave has a token colour. The colours move on at each lap.
// - jackpot ready: the lamps of the jackpot shots light in turn, slowly and at low contrast.
// - mode wash: a mode start tints every lamp with the mode colour in a short, soft pulse (WASH_MS).
// A lamp that is ready (mode, lock, wizard) breathes with a smooth fade, not a hard blink.
// The show counts its own time from the gaps between calls. A pause (a gap longer than
// PAUSE_GAP_MS) does not move the show on, so the lamps do not jump after a pause.
//
// Reduced motion and the effect level "reduced" or "off" turn the whole show off: `update` with
// `active` false clears every accent and tint. The lamps then show the rules state only.

import type { Color } from "three";
import type { InsertState } from "../lights.ts";
import { colorOf, SHOW_HEX } from "./palette.ts";

/** Does a game run? */
export type LampPhase = "attract" | "play";

/** A lamp, its place and its insert. `x` and `z` are three.js coordinates. */
export interface LampSlot {
  readonly name: string;
  readonly insert: InsertState;
  readonly x: number;
  readonly z: number;
}

/** Which lamps play a part in the show. */
export interface LampRoles {
  /** When this lamp is lit, the jackpot is ready. */
  readonly jackpot: string;
  /** The lamps that chase while the jackpot is ready. */
  readonly jackpotSet: readonly string[];
  /** The lamps that breathe while they are lit or blinking. */
  readonly ready: readonly string[];
}

/** The roles of the table "SigmaShake Pinball". */
export const DEFAULT_ROLES: LampRoles = {
  jackpot: "SaucerJackpotLamp",
  jackpotSet: [
    "SaucerJackpotLamp",
    "ShotLampRampLeft",
    "ShotLampRampRight",
    "ShotLampOrbitLeft",
    "ShotLampOrbitRight",
  ],
  ready: ["SaucerModeLamp", "SaucerLockLamp", "SaucerWizardLamp"],
};

/** A hit glow fades in this time. */
export const HIT_MS = 450;
/** A lamp glows again for a hit after this time at the earliest (3 times in 1 s at most). */
export const HIT_GAP_MS = 340;
/** The attract wave moves on by one lamp after this time. */
export const CHASE_STEP_MS = 70;
/** Lamps in the tail of one attract wave. */
export const CHASE_TAIL = 9;
/** Waves that run at one time in attract. They have equal gaps, and each has its own colour. */
export const CHASE_HEADS = 2;
/** The colours of the attract waves, in turn. Each lap of the table moves them on by one. */
const CHASE_COLOURS: readonly Color[] = SHOW_HEX.map(colorOf);
/** The jackpot chase moves on by one lamp after this time. A slow step keeps the chase calm. */
export const JACKPOT_STEP_MS = 480;
/** Accent of the lamp whose turn it is in the jackpot chase. */
export const JACKPOT_TURN_ACCENT = 0.6;
/** Accent of the other lamps of the jackpot set. */
export const JACKPOT_IDLE_ACCENT = 0.2;
/** A mode wash is a short pulse. It fades in this time. */
export const WASH_MS = 600;
/** Accent of a mode wash at its start. Low, so the wash is a soft pulse and not a flash. */
export const WASH_PEAK = 0.35;
/** A gap between two calls that is longer than this is a pause. The show does not count a pause. */
export const PAUSE_GAP_MS = 1000;
/** A ready lamp breathes with this slow period. */
export const BREATH_MS = 2500;
/** A ready lamp breathes from this accent to the next one. */
export const BREATH_MIN = 0.35;
export const BREATH_MAX = 0.65;

/** The lamp show. */
export interface LampShow {
  readonly phase: LampPhase;
  setPhase(phase: LampPhase): void;
  /** A hit at the lamp of an element. A null or unknown lamp does nothing. */
  hit(lampName: string | null, nowMs: number): void;
  /** Tint every lamp with a colour. The colour must stay as it is until the wash ends. */
  wash(color: Color, nowMs: number): void;
  /** Write the accents. `active` false clears them. */
  update(nowMs: number, active: boolean): void;
}

/** The state of one lamp show. The helpers below read and write it. */
interface LampCtx {
  readonly slots: readonly LampSlot[];
  readonly count: number;
  readonly indexOf: Map<string, number>;
  /** The place of each lamp in the attract wave. */
  readonly rank: Int32Array;
  readonly hitAt: Float64Array;
  readonly isReady: Uint8Array;
  readonly jackpotRank: Int32Array;
  readonly jackpotIndexes: readonly number[];
  readonly jackpotLamp: number | undefined;
  /** The attract waves write here once for each update. `chaseColour` holds shared Colors or null. */
  readonly chaseAccent: Float32Array;
  readonly chaseColour: (Color | null)[];
  phase: LampPhase;
  washColor: Color | null;
  washAt: number;
  dirty: boolean;
  /** The last wall-clock time that the show saw. NaN before the first call. */
  wallAt: number;
  /** The show time in ms. It moves on by the gaps between calls, but not by a pause. */
  clock: number;
}

/** Make the state for a list of lamps. */
function lampShowContext(slots: readonly LampSlot[], roles: LampRoles): LampCtx {
  const count = slots.length;
  const indexOf = new Map<string, number>();
  slots.forEach((slot, i) => {
    indexOf.set(slot.name, i);
  });
  // The attract wave runs from the top of the table to the bottom, left to right in a row.
  const order = slots.map((_, i) => i).sort((a, b) => rowOrder(slots[a], slots[b]));
  const rank = new Int32Array(count);
  order.forEach((slotIndex, r) => {
    rank[slotIndex] = r;
  });
  const isReady = new Uint8Array(count);
  for (const name of roles.ready) {
    const i = indexOf.get(name);
    if (i !== undefined) isReady[i] = 1;
  }
  const jackpotRank = new Int32Array(count).fill(-1);
  const jackpotIndexes = roles.jackpotSet
    .map((name) => indexOf.get(name))
    .filter((i): i is number => i !== undefined);
  jackpotIndexes.forEach((slotIndex, r) => {
    jackpotRank[slotIndex] = r;
  });
  return {
    slots,
    count,
    indexOf,
    rank,
    hitAt: new Float64Array(count).fill(Number.NEGATIVE_INFINITY),
    isReady,
    jackpotRank,
    jackpotIndexes,
    jackpotLamp: indexOf.get(roles.jackpot),
    chaseAccent: new Float32Array(count),
    chaseColour: new Array<Color | null>(count).fill(null),
    phase: "attract",
    washColor: null,
    washAt: Number.NEGATIVE_INFINITY,
    dirty: false,
    wallAt: Number.NaN,
    clock: 0,
  };
}

/**
 * The show time at a wall-clock time. The show adds the gap since its last call. A gap longer than
 * PAUSE_GAP_MS is a pause (a held draft, a hidden tab): the show stands still across it, so the
 * lamps do not jump on resume.
 */
function lampShowTime(ctx: LampCtx, wallMs: number): number {
  if (!Number.isFinite(wallMs)) return ctx.clock;
  const gap = wallMs - ctx.wallAt;
  if (gap > 0 && gap <= PAUSE_GAP_MS) ctx.clock += gap;
  ctx.wallAt = wallMs;
  return ctx.clock;
}

function lampShowHitAccent(ctx: LampCtx, i: number, now: number): number {
  const age = now - (ctx.hitAt[i] as number);
  return age >= 0 && age < HIT_MS ? 1 - age / HIT_MS : 0;
}

/** Work out the accent and the colour of every lamp for the attract waves. */
function lampShowFillChase(ctx: LampCtx, now: number): void {
  const { count, rank, chaseAccent, chaseColour } = ctx;
  chaseAccent.fill(0);
  chaseColour.fill(null);
  if (ctx.phase !== "attract" || count === 0) return;
  const step = Math.floor(now / CHASE_STEP_MS);
  const lap = Math.floor(step / count);
  const gap = Math.max(1, Math.floor(count / CHASE_HEADS));
  for (let h = 0; h < CHASE_HEADS; h++) {
    const head = (step + h * gap) % count;
    const colour = CHASE_COLOURS[(h + lap) % CHASE_COLOURS.length] as Color;
    for (let i = 0; i < count; i++) {
      const behind = (head - (rank[i] as number) + count) % count;
      const accent = behind < CHASE_TAIL ? (1 - behind / CHASE_TAIL) * 0.95 : 0;
      if (accent <= (chaseAccent[i] as number)) continue;
      chaseAccent[i] = accent;
      chaseColour[i] = colour;
    }
  }
}

function lampShowJackpotAccent(ctx: LampCtx, i: number, now: number, ready: boolean): number {
  const r = ctx.jackpotRank[i] as number;
  if (!ready || r < 0) return 0;
  const turn = Math.floor(now / JACKPOT_STEP_MS) % ctx.jackpotIndexes.length;
  return r === turn ? JACKPOT_TURN_ACCENT : JACKPOT_IDLE_ACCENT;
}

function lampShowReadyAccent(ctx: LampCtx, i: number, now: number): number {
  if (ctx.isReady[i] !== 1 || (ctx.slots[i] as LampSlot).insert.state === 0) return 0;
  const wave = 0.5 + 0.5 * Math.sin((now / BREATH_MS) * Math.PI * 2);
  return BREATH_MIN + (BREATH_MAX - BREATH_MIN) * wave;
}

function lampShowWashAccent(ctx: LampCtx, now: number): number {
  const left = 1 - (now - ctx.washAt) / WASH_MS;
  return ctx.washColor !== null && left > 0 && left <= 1 ? left * WASH_PEAK : 0;
}

function lampShowWrite(ctx: LampCtx, now: number): void {
  const { slots, count, chaseAccent, chaseColour } = ctx;
  const jackpotReady =
    ctx.jackpotLamp !== undefined && (slots[ctx.jackpotLamp] as LampSlot).insert.state !== 0;
  const wash = lampShowWashAccent(ctx, now);
  lampShowFillChase(ctx, now);
  for (let i = 0; i < count; i++) {
    const insert = (slots[i] as LampSlot).insert;
    insert.accent = Math.max(
      lampShowHitAccent(ctx, i, now),
      chaseAccent[i] as number,
      lampShowJackpotAccent(ctx, i, now, jackpotReady),
      lampShowReadyAccent(ctx, i, now),
      wash,
    );
    insert.tint = wash > 0 ? ctx.washColor : (chaseColour[i] as Color | null);
  }
}

function lampShowClear(ctx: LampCtx): void {
  for (const slot of ctx.slots) {
    slot.insert.accent = 0;
    slot.insert.tint = null;
  }
}

function lampShowHit(ctx: LampCtx, lampName: string | null, nowMs: number): void {
  const now = lampShowTime(ctx, nowMs);
  const i = lampName === null ? undefined : ctx.indexOf.get(lampName);
  if (i === undefined) return;
  if (now - (ctx.hitAt[i] as number) >= HIT_GAP_MS) ctx.hitAt[i] = now;
}

function lampShowUpdate(ctx: LampCtx, nowMs: number, active: boolean): void {
  const now = lampShowTime(ctx, nowMs);
  if (active) {
    lampShowWrite(ctx, now);
    ctx.dirty = true;
  } else if (ctx.dirty) {
    lampShowClear(ctx);
    ctx.dirty = false;
  }
}

/** Make the show for a list of lamps. */
export function createLampShow(
  slots: readonly LampSlot[],
  roles: LampRoles = DEFAULT_ROLES,
): LampShow {
  const ctx = lampShowContext(slots, roles);
  return {
    get phase() {
      return ctx.phase;
    },
    setPhase(next) {
      ctx.phase = next;
    },
    hit(lampName, nowMs) {
      lampShowHit(ctx, lampName, nowMs);
    },
    wash(color, nowMs) {
      ctx.washColor = color;
      ctx.washAt = lampShowTime(ctx, nowMs);
    },
    update(nowMs, active) {
      lampShowUpdate(ctx, nowMs, active);
    },
  };
}

/** Order of two lamps in the attract wave: by row (z), then by column (x). */
function rowOrder(a: LampSlot | undefined, b: LampSlot | undefined): number {
  if (!a || !b) return 0;
  const row = Math.round(a.z / 60) - Math.round(b.z / 60);
  return row !== 0 ? row : a.x - b.x;
}
